// src/components/scambi/ScambiLiberiTab.js
// Scambi liberi: nessuna asta di riferimento. L'utente sceglie due gruppi di
// giocatori qualsiasi dal database globale e ottiene subito un'analisi
// guadagno/perdita (motivata). Da qui può anche renderla pubblica tramite
// PublishTradeControl (condiviso con Win-To-Win) — il feed/voto/community
// vive ora nel tab separato ScambiCommunityTab.
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Loader2, RotateCcw, Search, X } from 'lucide-react';
import { getPlayersFromFirestore } from '../../firebase/playerService';
import { applyFantAppRoleOverrides } from '../../utils/fantAppRoleOverrides';
import TeamBadge from '../TeamBadge';
import { buildRoleOverrideMap, applyRoleOverrideToPlayer } from '../auction/auctionRosterUtils';
import { DEFAULT_MANTRA_ROLE_CONFIGURATION } from '../auction/MantraRoleConfiguration';
import { TradeAnalysisReport, PublishTradeControl, wrapPlayerAsPurchase, PlayerRoleBadge } from './ScambiCommon';

function normalizeText(value = '') {
  return String(value).toLowerCase().trim().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

function PlayerSearchPicker({ label, players, selectedPlayers, onToggle, excludeIds, rolesMode }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return [];
    return players
      .filter((p) => !excludeIds.has(String(p.id)))
      .filter((p) => normalizeText(`${p.name} ${p.team}`).includes(q))
      .slice(0, 20);
  }, [players, search, excludeIds]);

  return (
    <div className="scambi-player-picker">
      <div className="scambi-player-picker__head"><strong>{label}</strong></div>

      {selectedPlayers.length > 0 && (
        <div className="scambi-player-picker__chips">
          {selectedPlayers.map((p) => (
            <span key={p.id} className="scambi-player-chip">
              <TeamBadge team={p.team} size={20} />
              {p.name}
              <PlayerRoleBadge role={p.role} rolesMode={rolesMode} />
              <button type="button" onClick={() => onToggle(p)} aria-label={`Rimuovi ${p.name}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <label className="market-search">
        <Search size={16} />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca giocatore per nome o squadra" />
      </label>

      {search && (
        <div className="scambi-player-picker__results">
          {filtered.length === 0 ? (
            <div className="scambi-player-picker__empty">Nessun giocatore trovato.</div>
          ) : (
            filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                className="scambi-player-picker__result"
                onClick={() => { onToggle(p); setSearch(''); }}
              >
                <TeamBadge team={p.team} size={26} />
                <span className="scambi-player-picker__resultBody">
                  <strong>{p.name}</strong>
                  <span>{p.team}</span>
                </span>
                <PlayerRoleBadge role={p.role} rolesMode={rolesMode} />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function ScambiLiberiTab({ onGoToCommunity }) {
  const [players, setPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [rolesMode, setRolesMode] = useState('classic');

  const [offered, setOffered] = useState([]);
  const [received, setReceived] = useState([]);
  const [analyzed, setAnalyzed] = useState(null);
  const [analysisVersion, setAnalysisVersion] = useState(0);

  useEffect(() => {
    getPlayersFromFirestore()
      .then((list) => setPlayers(applyFantAppRoleOverrides(list)))
      .catch(() => setPlayers([]))
      .finally(() => setLoadingPlayers(false));
  }, []);

  const mantraRoleMap = useMemo(
    () => buildRoleOverrideMap({ roleOverrides: DEFAULT_MANTRA_ROLE_CONFIGURATION }),
    []
  );

  // In Mantra i giocatori portano i token mantra come `role`, così i chip e il
  // motore scambi (che bucketizza sul ruolo) ragionano sui reparti mantra.
  const effectivePlayers = useMemo(() => {
    if (rolesMode !== 'mantra') return players;
    return players.map((p) => applyRoleOverrideToPlayer(p, mantraRoleMap));
  }, [players, rolesMode, mantraRoleMap]);

  const handleModeChange = (next) => {
    if (next === rolesMode) return;
    setRolesMode(next);
    setOffered([]);
    setReceived([]);
    setAnalyzed(null);
  };

  const excludeIds = useMemo(
    () => new Set([...offered, ...received].map((p) => String(p.id))),
    [offered, received]
  );

  const toggleOffered = (p) => {
    setOffered((prev) => (prev.some((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p]));
  };
  const toggleReceived = (p) => {
    setReceived((prev) => (prev.some((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p]));
  };

  const handleAnalyze = () => {
    if (!offered.length || !received.length) return;
    setAnalyzed({
      mySelected: offered.map(wrapPlayerAsPurchase),
      theirSelected: received.map(wrapPlayerAsPurchase),
      rolesMode,
    });
    setAnalysisVersion((v) => v + 1);
  };

  const handleReset = () => {
    setOffered([]);
    setReceived([]);
    setAnalyzed(null);
  };

  return (
    <div className="scambi-liberi">
      <div className="scambi-liberi__modeRow">
        <span className="scambi-liberi__modeLabel">Ruoli</span>
        <div className="scambi-mode-toggle" role="group" aria-label="Modalità ruoli">
          <button
            type="button"
            className={rolesMode === 'classic' ? 'is-active' : ''}
            aria-pressed={rolesMode === 'classic'}
            onClick={() => handleModeChange('classic')}
          >
            Classic
          </button>
          <button
            type="button"
            className={rolesMode === 'mantra' ? 'is-active' : ''}
            aria-pressed={rolesMode === 'mantra'}
            onClick={() => handleModeChange('mantra')}
          >
            Mantra
          </button>
        </div>
        <span className="scambi-liberi__modeHint">
          {rolesMode === 'mantra'
            ? 'I giocatori e l’analisi ragionano sui ruoli Mantra (Dc, W/T, Pc…).'
            : 'I giocatori e l’analisi ragionano sui ruoli Classic (P/D/C/A).'}
        </span>
      </div>

      <div className="analysis-trade__panel analysis-trade__panel--compare">
        <div className="trade-compare-columns">
          <div className="trade-picker glass">
            <div className="trade-picker__head">
              <strong>Offri</strong>
              <span>I giocatori che daresti via</span>
            </div>
            {loadingPlayers ? (
              <div className="scambi-loading"><Loader2 size={18} className="spin" /> Carico i giocatori...</div>
            ) : (
              <PlayerSearchPicker
                label="Cerca giocatore"
                players={effectivePlayers}
                selectedPlayers={offered}
                onToggle={toggleOffered}
                excludeIds={excludeIds}
                rolesMode={rolesMode}
              />
            )}
          </div>

          <div className="trade-picker glass">
            <div className="trade-picker__head">
              <strong>Ricevi</strong>
              <span>I giocatori che vorresti in cambio</span>
            </div>
            {loadingPlayers ? (
              <div className="scambi-loading"><Loader2 size={18} className="spin" /> Carico i giocatori...</div>
            ) : (
              <PlayerSearchPicker
                label="Cerca giocatore"
                players={effectivePlayers}
                selectedPlayers={received}
                onToggle={toggleReceived}
                excludeIds={excludeIds}
                rolesMode={rolesMode}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="confirm-btn analysis-trade__analyzeBtn"
          disabled={!offered.length || !received.length}
          onClick={handleAnalyze}
        >
          <ArrowRightLeft size={16} />
          Analizza scambio
        </button>

        <button
          type="button"
          className="reset-btn"
          disabled={!offered.length && !received.length && !analyzed}
          onClick={handleReset}
        >
          <RotateCcw size={15} />
          Reset scambio
        </button>

        {analyzed && (
          <div className="proposal-result glass">
            <TradeAnalysisReport
              mySelected={analyzed.mySelected}
              theirSelected={analyzed.theirSelected}
              myTeamName="Tu"
              theirTeamName="La controparte"
              rolesMode={analyzed.rolesMode}
              auction={null}
              myRosterCounts={null}
              theirRosterCounts={null}
              showCredits={false}
            />

            <PublishTradeControl
              key={analysisVersion}
              mySelected={analyzed.mySelected}
              theirSelected={analyzed.theirSelected}
              myTeamName="Tu"
              theirTeamName="La controparte"
              rolesMode={analyzed.rolesMode}
              auction={null}
              myRosterCounts={null}
              theirRosterCounts={null}
              kind="liberi"
              onGoToCommunity={onGoToCommunity}
            />
          </div>
        )}
      </div>
    </div>
  );
}
